import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Tab, Tabs } from "@material-ui/core";
import { Grid } from "../../../component/list/grid";
import DefaultButton from "../../../component/base/DefaultButton";
import { RiArrowGoBackLine } from "react-icons/ri";
import { ModalViewResponse } from "../student-responses-dissertatives/components/ModalViewResponse";
import { useStudentResponseDissertatives } from "../../../hooks/studentResponseDissertatives/useStudentResponseDissertatives";
import {
  mapDifficultyLevel,
  mapEducationLevel,
} from "../../../utils/questions/questionsMap";
import type { Pagination } from "../../../services/stores/types";

var debounce = require("lodash.debounce");

export function StudentResponsesDissertativesView() {
  const { applicationId, studentId } = useParams();
  const token = localStorage.getItem("token") || "";

  const { getStudentResponsesByStudent } =
    useStudentResponseDissertatives(token);

  const [studentResponses, setStudentResponses] = useState<any[]>([]);
  const [studentName, setStudentName] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 20,
    totalItems: 0,
  });
  const [loadingResponses, setLoadingResponses] = useState<boolean>(true);

  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isSearching, setIsSearching] = useState<boolean>(false);

  const [modalQuestionId, setModalQuestionId] = useState<string | null>(null);
  const [showModalManage, setShowModalManage] = useState<boolean>(false);

  const [activeTab, setActiveTab] = useState<number>(0);

  const tabs = useMemo(
    () => [
      { label: "Todos", status: null },
      { label: "Corretas", status: "correct" },
      { label: "Incorretas", status: "incorrect" },
      { label: "Não Corrigidas", status: "undefinedByReviewer" },
    ],
    []
  );

  const fetchResponsesByStatus = useCallback(
    async (status: string | null, page = 1, searchTerm?: string) => {
      if (!applicationId || !studentId) return;
      setLoadingResponses(true);
      try {
        const term = searchTerm !== undefined ? searchTerm : searchQuery;
        const resp = await getStudentResponsesByStudent(
          applicationId,
          studentId,
          page,
          pagination.itemsPerPage,
          status ?? undefined,
          term
        );
        setStudentResponses(resp.responses);
        setPagination(resp.pagination);
        if (resp.responses.length && resp.responses[0].student?.name) {
          setStudentName(resp.responses[0].student.name);
        }
      } catch (e) {
        console.error("Erro ao buscar respostas por status:", e);
      } finally {
        setLoadingResponses(false);
      }
    },
    [
      applicationId,
      studentId,
      getStudentResponsesByStudent,
      pagination.itemsPerPage,
      searchQuery,
    ]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      const trimmed = value.trim();
      const isNumeric = /^\d+$/.test(trimmed);

      if (
        (isNumeric && trimmed.length >= 1) ||
        (!isNumeric && trimmed.length >= 3)
      ) {
        setSearchQuery(trimmed);
        fetchResponsesByStatus(tabs[activeTab].status, 1, trimmed);
      } else {
        setSearchQuery("");
        setPagination((p) => ({ ...p, currentPage: 1 }));
        fetchResponsesByStatus(tabs[activeTab].status, 1, "");
      }
      setIsSearching(false);
    }, 500),
    [fetchResponsesByStatus, tabs, activeTab]
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  useEffect(() => {
    fetchResponsesByStatus(tabs[activeTab].status, 1);
  }, [fetchResponsesByStatus, tabs, activeTab]);

  const getStatusMessage = (response: any) => {
    if (response.isWrong === true) return "Correta";
    if (response.isWrong === false) return "Incorreta";
    if (response.isWrong === null) return "Não Corrigida";
    if (response.status?.correct) return "Correta";
    if (response.status?.incorrect) return "Incorreta";
    if (response.status?.undefinedByReviewer) return "Não Corrigida";
    return "";
  };

  const gridItems = studentResponses.map((response) => {
    const statement =
      response.subquestions?.[0]?.statement.replace(/<\/?[^>]+(>|$)/g, "") ||
      "Sem enunciado";
    return {
      _id: response.id,
      questionId: response.question?.id || "N/A",
      statement,
      numberQuestion:
        response.page && response.questionCount
          ? `P${response.page}-Q${response.questionCount}`
          : "N/A",
      educationLevel:
        mapEducationLevel(response.question?.educationLevel || "") ||
        "Não informado",
      difficultyLevel:
        mapDifficultyLevel(response.question?.difficultyLevel || "") ||
        "Não informado",
      studentName: response.student?.name || "Não informado",
      statusMessage: getStatusMessage(response),
      createdAt: response.createdAt,
    };
  });

  const handleChangePagination = useCallback(
    (newPage: number) => {
      fetchResponsesByStatus(tabs[activeTab].status, newPage);
    },
    [fetchResponsesByStatus, tabs, activeTab]
  );

  const handleChangeTab = useCallback(
    (_: React.ChangeEvent<{}>, newIndex: number) => {
      setActiveTab(newIndex);
      fetchResponsesByStatus(tabs[newIndex].status, 1);
    },
    [fetchResponsesByStatus, tabs]
  );

  const handleManagerActionClick = useCallback((id: string) => {
    setModalQuestionId(id);
    setShowModalManage(true);
  }, []);

  const handleCloseModalManage = () => {
    setShowModalManage(false);
    setModalQuestionId(null);
  };

  return (
    <div className="container-fluid py-3 px-3 bg-light">
      <header className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold">
            Respostas Dissertativas do Aluno: {studentName || "Não definido"}
          </h2>
          <p className="text-muted">
            Confira abaixo as respostas dissertativas enviadas pelo aluno.
          </p>
        </div>
        <DefaultButton
          bg="secondary"
          text="Voltar"
          icon={<RiArrowGoBackLine />}
          to="-1"
        />
      </header>

      <Tabs
        value={activeTab}
        onChange={handleChangeTab}
        style={{ backgroundColor: "#fafafa", marginBottom: "1rem" }}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="on"
      >
        {tabs.map((tab, i) => (
          <Tab key={i} label={tab.label} />
        ))}
      </Tabs>

      <Grid
        search_input_value={searchInputValue}
        search_text="Pesquisar por enunciado ou número da questão"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={() =>
          fetchResponsesByStatus(tabs[activeTab].status, 1)
        }
        loading_search={isSearching}
        loading_screen={loadingResponses}
        fields={[
          { name: "statement", type: "shortText", header: "Enunciado" },
          { name: "difficultyLevel", type: "text", header: "Dificuldade" },
          { name: "statusMessage", type: "statusMessage", header: "Status" },
        ]}
        items={gridItems}
        hideManager
        onEditActionClick={handleManagerActionClick}
        canEditItem={() => true}
        not_found_message="Nenhuma resposta encontrada."
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        onChangePagination={handleChangePagination}
        top_text="Respostas dissertativas cadastradas"
      />

      <ModalViewResponse
        responseId={modalQuestionId || ""}
        token={token}
        isOpen={showModalManage}
        onClose={handleCloseModalManage}
      />
    </div>
  );
}
